import gradAddonEligible from '../../../../utils/gradAddonEligibility';

/**
 * Which kind of order is this customer in — senior, school pictures, or sports?
 *
 * ⚠️ THERE IS NO ORDER-TYPE FIELD ANYWHERE. This resolves one from what the page
 * already knows, and the inputs are messier than they look:
 *
 *   `pricelistSeason`  free text, not an enum. Real values in production include
 *                      `seniors2026`, `fall2026`, `spring2026`, and for sports
 *                      FOUR different spellings -- `sportsfall2026`,
 *                      `sportfall2025`, `sportswinter2026`, `sportWinter2024`.
 *                      Hence a case-insensitive /sport/ rather than a list.
 *   `schoolTypeId`     1 High School, 2 Middle, 3 Elementary, 4 Sports (not
 *                      affiliated with a school), 5 Other, 6 District.
 *
 * ONE implementation, here, and it delegates the senior question to
 * `gradAddonEligible` rather than asking it again -- that function already
 * decides senior-ness for the product filter, and a second senior test would be
 * free to disagree with the first about which products a customer can see.
 *
 * ⚠️ THE SEASON LEADS, THE SCHOOL ONLY BREAKS TIES. The school says who the
 * customer is; the PRICELIST says what they are buying, and a senior at a high
 * school buying a sports package is on a sports order. So no season means no
 * active order and the answer is UNKNOWN -- not "high school, therefore school
 * pictures", which would put a picture-day banner over a direct visitor who is
 * not in any order at all.
 */

export const ORDER_TYPES = {
  SENIOR: 'senior',
  SCHOOL: 'school',
  SPORTS: 'sports',
  UNKNOWN: 'unknown',
};

const SCHOOL_TYPE_SPORTS = 4;
const SCHOOL_TYPE_K12 = [1, 2, 3];

export default function resolveOrderType({ pricelistSeason = null, schoolTypeId = null, poses = [] } = {}) {
  const season = (pricelistSeason || '').trim();

  // No pricelist means no order in progress. Say so rather than guessing from
  // the school the student happens to attend.
  if (!season) return ORDER_TYPES.UNKNOWN;

  if (/sport/i.test(season)) return ORDER_TYPES.SPORTS;

  // Before the school check: a senior IS at a high school, so testing the
  // school first would call every senior order "school pictures".
  if (gradAddonEligible({ pricelistSeason: season, poses })) return ORDER_TYPES.SENIOR;

  if (/^(fall|spring)/i.test(season)) return ORDER_TYPES.SCHOOL;

  // A season we do not recognise (`dance2025`, `composite`, `panorama`, …).
  // There IS an order, so the school is now worth something as a tiebreak.
  if (schoolTypeId === SCHOOL_TYPE_SPORTS) return ORDER_TYPES.SPORTS;
  if (SCHOOL_TYPE_K12.includes(schoolTypeId)) return ORDER_TYPES.SCHOOL;

  return ORDER_TYPES.UNKNOWN;
}
